import React, { useState, useCallback, useEffect } from "react";
import Select, { OptionsType } from "react-select";
import SearchTopics from "../../../graphql/queries/topic/SearchTopics";
import { debounce } from "lodash";
import { Sector, Vendor } from "../insightsProduct";

export type Option = {
  id: string;
  name: string;
  type: "Vendor" | "Sector";
  slug: string;
};

export type TopicSelectValue = OptionsType<Option>;

interface TopicSelectProps {
  value: Vendor[] | Sector[];
  onChange: (value: TopicSelectValue | null) => void;
  type: "Vendor" | "Sector";
}

const TopicSelect: React.FC<TopicSelectProps> = (props) => {
  const { value, type, onChange } = props;
  const [query, setQuery] = useState("");
  const [deferredQuery, setDeferredQuery] = useState("");

  const debouncedSetQuery = useCallback(debounce(setDeferredQuery, 250), []);

  useEffect(() => {
    debouncedSetQuery(query);
  }, [query]);

  const getDefaultValue = (): Option[] => {
    if (type === "Vendor") {
      const vendors = value as Vendor[];
      return vendors.map((v) => ({
        id: v.vendorId,
        slug: v.slug,
        name: v.name,
        type: "Vendor",
      })) as Option[];
    }

    if (type === "Sector") {
      const sectors = value as Sector[];
      return sectors.map((v) => ({
        id: v.sectorId,
        slug: v.slug,
        name: v.name,
        type: "Sector",
      })) as Option[];
    }

    return [];
  };

  return (
    <SearchTopics query={deferredQuery} type={type}>
      {({ data, loading, fetchMore }) => {
        const hasTopics = data?.topics?.length;
        const options = hasTopics
          ? data.topics.map((topic) => ({
              id: topic.id,
              name: topic.name,
              slug: topic.slug[0],
              type: topic.type,
            }))
          : [];

        return (
          <Select
            placeholder={
              type === "Vendor" ? "Select Vendors..." : "Select Sectors..."
            }
            defaultValue={getDefaultValue()}
            onChange={onChange}
            isLoading={loading}
            onInputChange={(value) => setQuery(value)}
            closeMenuOnSelec={false}
            isMulti={true}
            options={options}
            getOptionValue={(opt) => opt.id}
            getOptionLabel={(opt) => opt.name}
            onMenuScrollToBottom={() =>
              fetchMore({
                variables: { offset: options.length },
                updateQuery: (previousResults, { fetchMoreResult }) => {
                  if (!fetchMoreResult?.topics.length) {
                    return previousResults;
                  }

                  return {
                    topics: [
                      ...previousResults.topics,
                      ...fetchMoreResult.topics,
                    ],
                  };
                },
              })
            }
          />
        );
      }}
    </SearchTopics>
  );
};

export default TopicSelect;
